import { useState } from 'react'
import { motion } from 'framer-motion'
import { CheckCircle, Mail } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { useLocale } from '../../i18n/LocaleProvider'
import {
  confirmSignUp,
  getCognitoErrorMessage,
  resendConfirmationCode,
} from '../../lib/cognito'
import {
  isExpiredVerificationCodeError,
  normalizeEmail,
} from '../../lib/emailVerification'

export function EmailVerificationForm({
  email,
  onEmailChange,
  onConfirmed,
  submitLabel,
  allowEmailEdit = false,
}) {
  const { t } = useLocale()
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [codeExpired, setCodeExpired] = useState(false)

  const normalizedEmail = normalizeEmail(email)

  async function handleVerify(e) {
    e.preventDefault()
    setError('')
    setMessage('')
    setCodeExpired(false)

    if (!normalizedEmail) {
      setError(t('auth.errors.requiredEmail'))
      return
    }
    if (!code.trim()) {
      setError(t('auth.errors.requiredFields'))
      return
    }

    setLoading(true)
    try {
      await confirmSignUp(normalizedEmail, code.trim())
      await onConfirmed?.(normalizedEmail)
    } catch (err) {
      setCodeExpired(isExpiredVerificationCodeError(err))
      setError(getCognitoErrorMessage(err, t))
    } finally {
      setLoading(false)
    }
  }

  async function handleResendCode() {
    setError('')
    setMessage('')

    if (!normalizedEmail) {
      setError(t('auth.errors.requiredEmail'))
      return
    }

    setResending(true)
    try {
      await resendConfirmationCode(normalizedEmail)
      setCodeExpired(false)
      setMessage(t('auth.verification.newCodeSent'))
    } catch (err) {
      setError(getCognitoErrorMessage(err, t))
    } finally {
      setResending(false)
    }
  }

  return (
    <>
      <div className="mb-6 text-center">
        <div className="w-16 h-16 rounded-full bg-brand-100 flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-brand-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">{t('auth.verification.title')}</h1>
        <p className="text-gray-500 mt-2">
          {t('auth.verification.instructions')}{' '}
          {normalizedEmail ? (
            <span className="font-semibold text-gray-700">{normalizedEmail}</span>
          ) : (
            t('auth.verification.emailFallback')
          )}
        </p>
      </div>

      {error && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5">
          <span className="text-red-600 text-sm font-medium">{error}</span>
          {codeExpired && (
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resending}
              className="block text-sm text-brand-600 hover:text-brand-700 font-semibold mt-2 disabled:opacity-50"
            >
              {resending ? t('auth.common.sending') : t('auth.verification.sendNewCode')}
            </button>
          )}
        </motion.div>
      )}

      {message && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-green-50 border border-green-200 rounded-xl p-3 mb-5">
          <span className="text-green-700 text-sm font-medium">{message}</span>
        </motion.div>
      )}

      <form onSubmit={handleVerify} className="space-y-4">
        {allowEmailEdit && (
          <div className="form-group">
            <label className="label">{t('auth.common.email')}</label>
            <input
              type="email"
              className="input"
              placeholder={t('auth.common.emailPlaceholder')}
              value={email}
              onChange={(e) => onEmailChange?.(e.target.value)}
              autoComplete="email"
            />
          </div>
        )}

        <div className="form-group">
          <label className="label">{t('auth.verification.code')}</label>
          <input
            type="text"
            className="input text-center text-2xl tracking-widest font-bold"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            autoFocus
          />
        </div>

        <Button type="submit" variant="primary" fullWidth size="lg" loading={loading} icon={<CheckCircle className="w-4 h-4" />}>
          {submitLabel || t('auth.verification.verifyEmail')}
        </Button>
      </form>

      <div className="mt-4 text-center">
        <p className="text-sm text-gray-500">
          {t('auth.verification.noCode')}{' '}
          <button type="button" onClick={handleResendCode} disabled={resending} className="text-brand-600 hover:text-brand-700 font-semibold disabled:opacity-50">
            {resending ? t('auth.common.sending') : t('auth.verification.resendCode')}
          </button>
        </p>
      </div>
    </>
  )
}
